import { Component, EventEmitter, Input, Output } from '@angular/core';
import isEqual from 'lodash/isEqual';
import { TSelectOption } from 'src/app/project/components/input/select/select.model';
import { TCustomField } from 'src/app/project/modules/custom-fields/custom-fields.model';
import { GET_CUSTOM_FIELDS } from 'src/app/project/modules/custom-fields/custom-fields.store';
import { EAdvancedFilterOptions } from '../../../../models/advanced-filter-options.enum';
import {
  TAdvancedFilterMultiList,
  TAdvancedFilterMultiListValue,
  TAdvancedFilterSingle,
  TFilterMultiListOptions,
} from '../../../../models/advanced-filter.model';
import {
  IS_EMPTY_ARRAY,
  IS_NOT_EMPTY_ARRAY,
  multiListOperatorOptions,
} from '../../../../utils/advanced-filter-operator.utils';

@Component({
  selector: 'pp-advanced-filter-multi-list',
  templateUrl: './advanced-filter-multi-list.component.html',
  styleUrls: ['./advanced-filter-multi-list.component.scss'],
  standalone: false,
})
export class AdvancedFilterMultiListComponent {
  @Input() ppFilter: TAdvancedFilterSingle;
  @Input() ppWorkspaceId: string;
  @Output() ppFilterChange = new EventEmitter<TAdvancedFilterSingle>();

  advancedFilterMultiListOptions: TSelectOption[];
  EAdvancedFilterOptions = EAdvancedFilterOptions;
  value: TAdvancedFilterMultiListValue;
  option: TFilterMultiListOptions;
  filter: TAdvancedFilterMultiList;
  unit: string;
  field: TCustomField;

  private lastEmittedSnapshot = '';

  ngOnChanges(): void {
    this.advancedFilterMultiListOptions = multiListOperatorOptions;

    const snapshot = JSON.stringify({
      value: this.ppFilter.value,
      option: this.ppFilter.option,
    });

    if (snapshot === this.lastEmittedSnapshot) {
      return;
    }

    this.filter = this.ppFilter as TAdvancedFilterMultiList;

    this.option = this.filter.option;
    this.field = GET_CUSTOM_FIELDS()[this.filter.id];
    this.handlePreselectedValues();

    this.applyChanges();
  }

  private findItemById(
    idToFind: string,
    list: TCustomField['subList'] | undefined,
  ): TCustomField['subList'][0] | null {
    if (!list) {
      return null;
    }

    for (const item of list) {
      if (item.id === idToFind) {
        return item;
      }

      const foundInSub = this.findItemById(idToFind, item.subList);
      if (foundInSub) {
        return foundInSub;
      }
    }
    return null;
  }

  private getAllSubListIds(list: TCustomField['subList'] | undefined): string[] {
    let ids: string[] = [];

    if (!list) {
      return [];
    }

    for (const item of list) {
      ids.push(item.id);

      if (item.subList && item.subList.length > 0) {
        ids = ids.concat(this.getAllSubListIds(item.subList));
      }
    }
    return ids;
  }

  private getAncestorIds(
    idToFind: string,
    list: TCustomField['subList'] | undefined,
    ancestors: string[] = [],
  ): string[] | null {
    if (!list) {
      return null;
    }

    for (const item of list) {
      if (item.id === idToFind) {
        return ancestors;
      }

      if (item.subList && item.subList.length > 0) {
        const found = this.getAncestorIds(idToFind, item.subList, [...ancestors, item.id]);
        if (found !== null) {
          return found;
        }
      }
    }
    return null;
  }

  private expandWithAncestors(ids: string[] | undefined | null): string[] {
    if (!ids || !this.field?.subList) {
      return ids ?? [];
    }

    const expanded = new Set<string>(ids);
    for (const id of ids) {
      const ancestors = this.getAncestorIds(id, this.field.subList);
      if (ancestors) {
        ancestors.forEach((ancestorId) => expanded.add(ancestorId));
      }
    }
    return Array.from(expanded);
  }

  private stripAncestors(ids: string[] | undefined | null): string[] {
    if (!ids || !this.field?.subList) {
      return ids ?? [];
    }

    const idSet = new Set(ids);
    return ids.filter((id) => {
      const item = this.findItemById(id, this.field.subList);
      return item ? !this.hasDescendantInSet(item, idSet) : true;
    });
  }

  private hasDescendantInSet(item: TCustomField['subList'][0], set: Set<string>): boolean {
    if (!item.subList || item.subList.length === 0) {
      return false;
    }
    for (const child of item.subList) {
      if (set.has(child.id)) {
        return true;
      }
      if (this.hasDescendantInSet(child, set)) {
        return true;
      }
    }
    return false;
  }

  handlePreselectedValues(): void {
    const value = this.filter.value;

    if (isEqual(value, IS_EMPTY_ARRAY.data) || isEqual(value, IS_NOT_EMPTY_ARRAY.data)) {
      if (
        this.filter.option === EAdvancedFilterOptions.IS_EMPTY ||
        this.filter.option === EAdvancedFilterOptions.IS_NOT_EMPTY
      ) {
        this.value = null;
      }

      return;
    }

    const selectedIds: string[] =
      Array.isArray(value) && value.every((item) => typeof item === 'string')
        ? value
        : value?.selectedIds || [];

    if (selectedIds.length === 0) {
      this.value = value;
      return;
    }

    const resultLabels: string[] = [];
    const ids = new Set<string>();
    const selectedIdSet = new Set(selectedIds);

    const userSelectedIds = selectedIds.filter((id) => {
      const item = this.findItemById(id, this.field?.subList);
      return item ? !this.hasDescendantInSet(item, selectedIdSet) : true;
    });

    for (const id of userSelectedIds) {
      if (ids.has(id)) {
        continue;
      }

      const item = this.findItemById(id, this.field.subList);
      if (!item) {
        continue;
      }

      const hasChildren = item.subList && item.subList.length > 0;
      let displayLabel = item.label;

      if (hasChildren) {
        displayLabel = `${item.label} (all)`;

        const allSubListIds = this.getAllSubListIds(item.subList);
        allSubListIds.forEach((descendantId) => ids.add(descendantId));
        ids.add(item.id);
      } else {
        displayLabel = item.label;
        ids.add(item.id);
      }

      resultLabels.push(displayLabel);
    }

    const uniqueLabels = Array.from(new Set(resultLabels));
    const subListLabel = uniqueLabels.join(', ');

    this.value = {
      id: this.filter.customFields?.[0],
      label: subListLabel,
      value: subListLabel,
      customFields: this.filter.customFields,
      selectedIds: userSelectedIds,
    };
  }

  changeFieldCondition(newCondition: TSelectOption): void {
    this.filter.option = newCondition.value as TFilterMultiListOptions;
    this.option = newCondition.value as TFilterMultiListOptions;

    if (
      newCondition.value === EAdvancedFilterOptions.IS_EMPTY ||
      newCondition.value === EAdvancedFilterOptions.IS_NOT_EMPTY
    ) {
      this.ppFilter.value = newCondition.data;
      this.value = null;
    }

    this.filter.operator = newCondition.operator;

    this.applyChanges();
  }

  applyChanges(): void {
    let ids: string[];
    if (
      this.filter.option === EAdvancedFilterOptions.IS_EMPTY ||
      this.filter.option === EAdvancedFilterOptions.IS_NOT_EMPTY
    ) {
      ids = IS_EMPTY_ARRAY.data as string[];
    } else {
      const baseIds = Array.isArray(this.filter.value)
        ? this.filter.value
        : this.filter.value?.selectedIds;
      const userIds = this.stripAncestors(baseIds);
      ids =
        this.filter.option === EAdvancedFilterOptions.IS_NOT_MULTI
          ? this.expandWithAncestors(userIds)
          : userIds;
    }

    this.ppFilter = {
      ...this.filter,
      value: ids,
    };

    this.lastEmittedSnapshot = JSON.stringify({
      value: this.ppFilter.value,
      option: this.ppFilter.option,
    });
    this.ppFilterChange.emit(this.ppFilter);
  }

  changeFieldValue(value: TAdvancedFilterMultiListValue): void {
    this.filter.value = value;
    this.applyChanges();
  }
}
